import { useCallback, useContext, useMemo } from "react";
// styles
import styles from "./ContractAction.module.scss";

// packages
import { useTranslation } from "react-i18next";

// context and providers
import { ContractContext } from "../../../providers/contract";
import { ActivityLogObj } from "./ActionsMap";
import { formatDateToArabic } from "../../../setup/util/helpers";
import {
  ESenderRole,
  IContractActionsResponseData,
} from "../../../setup/api/services/contract/models";

const ContractAction = () => {
  const { contract, contractActivities } = useContext(ContractContext);
  const { t } = useTranslation();

  const filteredContractActivities = useMemo(() => {
    return contractActivities?.filter(
      (contractActivity) => contractActivity.actionStatus === "COMPLETED"
    );
  }, [contractActivities]);

  const contractActivitesMap = useMemo(() => {
    return filteredContractActivities?.reduce((acc, item) => {
      acc[item.action] = item;
      return acc;
    }, {});
  }, [filteredContractActivities]);

  const contractStatus = useCallback(
    (action: string): string => {
      const actionMap: Record<string, string> = {
        "": t("CREATED_SUCCESSFULLY"),
        PROVIDER_CREATE_CONTRACT: t("CREATED_SUCCESSFULLY"),
        CUSTOMER_CREATE_CONTRACT: t("CREATED_SUCCESSFULLY"),
        BROKER_CREATE_CONTRACT: t("CREATED_SUCCESSFULLY"),
        CUSTOMER_FUND: t("lbl_buy_transaction"),
        CUSTOMER_CONFIRM_DELIVERY: t("lbl_confirm_delivery"),
        ADMIN_CONFIRM_DELIVERY: t("lbl_confirm_delivery"),
        CUSTOMER_ACCEPT_ITEM: t("lbl_confirm_inspection"),
        ADMIN_ACCEPT_ITEM: t("lbl_confirm_inspection"),
        CUSTOMER_REQUEST_PAYMENT_VERIFICATION: t(
          "lbl_customer_paid_with_manual_bank_transfer"
        ),
        ADMIN_VERIFY_PAYMENT: t("lbl_admin_verify_manual_bank_transfer"),
        ADMIN_DENY_PAYMENT: t("lbl_admin_deny_payments"),
        CUSTOMER_DENY_DELIVERY: t("lbl_buyer_escalate_delivery"),
        CUSTOMER_REJECT_ITEM: t("lbl_buyer_escalate_inspection"),
        ADMIN_RESOLVE_ESCALATION_REFUND_CUSTOMER: t(
          "lbl_admin_resolve_escalation_buyer"
        ),
        ADMIN_RESOLVE_ESCALATION_RELEASE_PROVIDER: t(
          "lbl_admin_resolve_escalation_seller"
        ),
        ADMIN_COMPLETE_RELEASE_FUND_TO_PROVIDER: t("lbl_money_transfer"),
        PROVIDER_ESCALATE_ITEM: t("lbl_provider_escalate_item"),
        PROVIDER_ESCALATE_DELIVERY: t("lbl_provider_escalate_delivery"),
        ADMIN_CLOSE_RELEASE_FUND_TO_PROVIDER: t(
          "lbl_admin_close_refund_to_provider"
        ),
        ADMIN_CLOSE_REFUND_CUSTOMER: t("lbl_admin_close_refund_to_buyer"),
      };

      return actionMap[action] || "";
    },
    [t]
  );

  const contractDate = useCallback(
    (action: string): string => {
      if (action === "") {
        return formatDateToArabic(contract?.createdAt?.toString() || "");
      }
      if (!contractActivitesMap) return "";
      if (!contractActivitesMap[action]?.createdAt) return "";
      const date = formatDateToArabic(
        contractActivitesMap[action]?.createdAt?.toString()
      );
      return date;
    },
    [contract?.createdAt, contractActivitesMap]
  );

  const renderDescription = useCallback(
    (activity) => {
      if (!contractActivitesMap) return null;

      const contractStatus = activity?.contractStatus;
      if (contractStatus === "") {
        return activity?.description?.replace(
          "{name}",
          contract?.provider?.profile?.fullName
        );
      }
      const activityMap = contractActivitesMap[contractStatus];

      if (!activityMap) return activity?.description;

      const { actorRole } = activityMap;
      // Define a helper function to get the full name
      const getUserName = () => {
        switch (actorRole) {
          case "ADMIN":
            return "Admin";
          case "CUSTOMER":
            return (
              contract?.customer?.profile?.fullName ||
              contract?.partiesProfiles?.CUSTOMER?.[0]?.profile?.fullName ||
              ""
            );
          case "PROVIDER":
            return (
              contract?.provider?.profile?.fullName ||
              contract?.partiesProfiles?.CUSTOMER?.[0]?.profile?.fullName ||
              ""
            );
          case "BROKER":
            return (
              contract?.partiesProfiles?.BROKER?.[0]?.profile?.fullName || ""
            );
          default:
            return "";
        }
      };

      const userName = getUserName();
      return activity?.description?.replace("{name}", userName);
    },
    [
      contract?.customer?.profile?.fullName,
      contract?.partiesProfiles?.BROKER,
      contract?.partiesProfiles?.CUSTOMER,
      contract?.provider?.profile?.fullName,
      contractActivitesMap,
    ]
  );

  const renderActivity = useCallback(
    (data: IContractActionsResponseData, index: number) => {
      if (!ActivityLogObj[data?.action]) return "";
      const activity = {
        contractStatus: data?.action,
        description: ActivityLogObj[data?.action],
      };
      if (!contractActivitesMap) return <></>;
      if (
        !contractActivitesMap[`${activity?.contractStatus}`] &&
        activity?.contractStatus !== ""
      )
        return <></>;
      if (
        activity?.contractStatus === "ADMIN_CLOSE_REFUND_CUSTOMER" &&
        contractActivitesMap[`${activity?.contractStatus}`]?.from !==
          "RESOLVED_REFUND_CUSTOMER"
      )
        return <></>;
      if (
        activity?.contractStatus === "ADMIN_CLOSE_RELEASE_FUND_TO_PROVIDER" &&
        contractActivitesMap[`${activity?.contractStatus}`]?.from !==
          "RESOLVED_RELEASE_PROVIDER"
      )
        return <></>;

      return (
        <div key={index} className={styles["activity-item"]}>
          <div className={styles["date"]}>
            <p>{contractDate(activity?.contractStatus)}</p>
          </div>
          <div className={styles["descrition"]}>
            <div className={styles["description_status"]}>
              <div className={styles["circle"]} />
              <div className={styles["line"]} />
            </div>
            <div className={styles["description_text"]}>
              <p className={styles["description_header"]}>
                {contractStatus(activity?.contractStatus)}
              </p>
              <p className={styles["description_subtitle"]}>
                {renderDescription(activity)}
              </p>
            </div>
          </div>
        </div>
      );
    },
    [contractActivitesMap, contractDate, contractStatus, renderDescription]
  );

  if (!contractActivitesMap || !filteredContractActivities) return null;

  return (
    <div className={styles["container"]}>
      <p className={styles["header"]}>{t("lbl_activity_log")}</p>
      <div className={styles["list_container"]}>
        <div className={styles["activities"]}>
          {filteredContractActivities?.length === 0 ? (
            <>
              {renderActivity(
                {
                  action: "",
                  actionStatus: "",
                  actorId: "",
                  actorRole: ESenderRole.PROVIDER,
                  createdAt: new Date(),
                  to: "",
                },
                0
              )}
            </>
          ) : (
            <>
              {filteredContractActivities?.map((activity, index) => (
                <>{renderActivity(activity, index)}</>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractAction;
